import React, { useState, useEffect } from 'react';
import networkService from '../services/networkService';
import syncService from '../services/syncService';
import './SyncStatus.css';

const SYNC_STUCK_MS = 95000; // чуть больше таймаута синка (90 сек)

const SyncStatus = () => {
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [syncError, setSyncError] = useState('');

  useEffect(() => {
    let syncStartTime = null;

    const initServices = async () => {
      try {
        await syncService.init();
        await updateStatus();
      } catch (error) {
        console.error('Error initializing sync services:', error);
      }
    };

    initServices();

    const networkListener = (online) => setIsOnline(online);

    networkService.addListener(networkListener);

    const syncListener = (status) => {
      if (status.status === 'syncing') {
        setIsSyncing(true);
        setSyncError('');
        syncStartTime = Date.now();
      } else {
        setIsSyncing(false);
        syncStartTime = null;
      }
      if (status.status === 'error' || status.status === 'auth_required') {
        setSyncError(status.error || (status.status === 'auth_required'
          ? 'Нужно войти в CRM заново'
          : 'Ошибка синхронизации'));
      }
      if (status.status === 'completed') setSyncError('');
      if (status.status === 'completed' || status.status === 'error') updateStatus();
    };

    syncService.addSyncListener(syncListener);

    const statusInterval = setInterval(() => {
      updateStatus();
      if (syncStartTime && Date.now() - syncStartTime > SYNC_STUCK_MS) {
        syncStartTime = null;
        setIsSyncing(false);
      }
    }, 5000);

    return () => {
      networkService.removeListener(networkListener);
      syncService.removeSyncListener(syncListener);
      clearInterval(statusInterval);
    };
  }, []);

  const updateStatus = async () => {
    try {
      const status = await syncService.getSyncStatus();
      setIsOnline(status.isOnline);
      setIsSyncing(status.isSyncing);
      setPendingCount(status.pendingCount);
      setLastSync(status.lastSync);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleManualSync = async () => {
    try {
      setIsSyncing(true);
      await syncService.syncAll({ force: true, source: 'manual' });
      await updateStatus();
    } catch (error) {
      console.error('Error during manual sync:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const formatLastSync = (lastSync) => {
    if (!lastSync) return 'Никогда';

    const date = new Date(lastSync);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Только что';
    if (diff < 3600) return `${Math.floor(diff / 60)} мин. назад`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч. назад`;
    return date.toLocaleDateString();
  };

  return (
    <div className="sync-status">
      <div className="sync-status-indicator">
        <div className={`sync-status-icon ${isOnline ? 'online' : 'offline'}`}>
          <span className="sync-status-dot"></span>
        </div>
        <div className="sync-status-info">
          <div className="sync-status-text">
            {isOnline ? 'Онлайн' : 'Оффлайн'}
          </div>
          {pendingCount > 0 && (
            <div className="sync-status-pending">
              {pendingCount} в очереди
              {isOnline && (
                <span className="sync-status-hint" title="Операции сохраняются до успешной отправки"> (ожидает отправки)</span>
              )}
            </div>
          )}
          {lastSync && (
            <div className="sync-status-last">
              {formatLastSync(lastSync)}
            </div>
          )}
          {syncError && (
            <div className="sync-status-error">
              {syncError}
            </div>
          )}
        </div>
      </div>
      {isOnline && (
        <button
          className="sync-status-button"
          onClick={handleManualSync}
          disabled={isSyncing}
        >
          {isSyncing ? 'Синхронизация...' : 'Синхронизировать'}
        </button>
      )}
    </div>
  );
};

export default SyncStatus;
